import { Link, useParams } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { api } from "../api";
import { Icon } from "../lib/Icon";
import { ErrorNote, Loading, Page } from "./common";

const KIND_ICON: Record<string, string> = { Article: "book", PDF: "file-signature", Video: "play" };

export function KbArticle() {
  const { articleId } = useParams({ from: "/kb/$articleId" });
  const { data, isLoading, error } = useQuery({ queryKey: ["kb-article", articleId], queryFn: () => api.kbArticle(articleId) });
  if (isLoading) return <Loading />;
  if (error || !data) return <ErrorNote error={error} />;

  const card = { background: "#fff", borderRadius: 3, boxShadow: "inset 0 0 0 1px rgba(0,0,0,.07)" } as const;

  return (
    <Page>
      <div style={{ display: "grid", gridTemplateColumns: "minmax(0,1fr) 260px", gap: 20, alignItems: "start" }}>
        <div>
          <Link to="/kb" style={{ fontSize: 12, color: "#8a8e88", textDecoration: "none" }}>← Knowledge</Link>
          <div style={{ display: "flex", alignItems: "center", gap: 9, marginTop: 8 }}>
            <span style={{ width: 30, height: 30, borderRadius: 7, background: "#e8f4ee", display: "flex", alignItems: "center", justifyContent: "center", flex: "none" }}>
              <Icon name={KIND_ICON[data.kind] ?? "book"} size={15} color="#006747" />
            </span>
            <h1 style={{ margin: 0, fontSize: 23, fontWeight: 600, letterSpacing: "-0.5px" }}>{data.title}</h1>
          </div>
          {data.subtitle && <div style={{ fontSize: 14, color: "#6b706a", marginTop: 8 }}>{data.subtitle}</div>}
          <div style={{ display: "flex", gap: 12, marginTop: 8, fontSize: 11.5, color: "#a8aca6" }}>
            <span>{data.kind}</span><span>{data.scope}</span><span>{data.views.toLocaleString()} views</span>
            {data.updated && <span>Updated {data.updated}</span>}
          </div>

          <div style={{ ...card, padding: "20px 22px", marginTop: 16 }}>
            {(data.body ?? "").split("\n").map((line, i) =>
              line.trim() === "" ? (
                <div key={i} style={{ height: 8 }} />
              ) : (
                <p key={i} style={{ margin: "0 0 8px", fontSize: 13.5, lineHeight: 1.6, color: "#2b312b" }}>{line}</p>
              ),
            )}
          </div>

          <div style={{ ...card, padding: "12px 18px", marginTop: 14, display: "flex", alignItems: "center", gap: 12 }}>
            <span style={{ fontSize: 12.5, color: "#8a8e88" }}>Was this helpful?</span>
            <span style={{ display: "inline-flex", alignItems: "center", gap: 5, fontSize: 12, fontWeight: 600, color: "#1f7a4d" }}><Icon name="check-circle" size={13} color="#1f7a4d" />Yes</span>
            <span style={{ display: "inline-flex", alignItems: "center", gap: 5, fontSize: 12, fontWeight: 600, color: "#8a8e88" }}><Icon name="circle" size={13} color="#8a8e88" />No</span>
          </div>
        </div>

        {/* Related */}
        <div style={{ ...card, padding: "14px 16px" }}>
          <div style={{ fontSize: 11, fontWeight: 600, textTransform: "uppercase", letterSpacing: ".5px", color: "#a8aca6", marginBottom: 8 }}>Related</div>
          {data.related.map((r) => (
            <Link key={r.id} to="/kb/$articleId" params={{ articleId: r.id }} style={{ display: "flex", gap: 8, alignItems: "center", padding: "8px 0", borderTop: "1px solid #f4f3ee", textDecoration: "none", color: "inherit" }}>
              <Icon name={KIND_ICON[r.kind] ?? "book"} size={13} color="#8a8e88" />
              <span style={{ fontSize: 12.5, color: "#4a504a" }}>{r.title}</span>
            </Link>
          ))}
          {data.related.length === 0 && <div style={{ fontSize: 12, color: "#bcbfba" }}>—</div>}
        </div>
      </div>
    </Page>
  );
}
